import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck, FaTimes, FaStar, FaUsers, FaHeadphones, FaBook, FaGraduationCap, FaGlobe, FaMobile, FaShieldAlt, FaClock, FaDollarSign, FaLanguage, FaHeart } from 'react-icons/fa';

interface ComparisonFeature {
  feature: string;
  aminePersia: boolean | string;
  rosettaStone: boolean | string;
  traditionalClass: boolean | string;
  selfStudy: boolean | string;
}

const Comparison: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const comparisonData: ComparisonFeature[] = [
    {
      feature: 'Native Persian Speakers',
      aminePersia: true,
      rosettaStone: false,
      traditionalClass: true,
      selfStudy: false
    },
    {
      feature: 'Cultural Context & Traditions',
      aminePersia: true,
      rosettaStone: false,
      traditionalClass: 'Limited',
      selfStudy: false
    },
    {
      feature: 'Persian Poetry (Hafez, Rumi)',
      aminePersia: true,
      rosettaStone: false,
      traditionalClass: 'Rare',
      selfStudy: 'Self-research'
    },
    {
      feature: 'Traditional Persian Recipes',
      aminePersia: true,
      rosettaStone: false,
      traditionalClass: false,
      selfStudy: false
    },
    {
      feature: 'Persian Holidays & Celebrations',
      aminePersia: true,
      rosettaStone: false,
      traditionalClass: 'Basic',
      selfStudy: false
    },
    {
      feature: 'Tehran City Guide & Culture',
      aminePersia: true,
      rosettaStone: false,
      traditionalClass: false,
      selfStudy: false
    },
    {
      feature: 'Interactive Flashcards',
      aminePersia: true,
      rosettaStone: true,
      traditionalClass: false,
      selfStudy: false
    },
    {
      feature: 'Adaptive Learning Algorithm',
      aminePersia: true,
      rosettaStone: true,
      traditionalClass: false,
      selfStudy: false
    },
    {
      feature: 'Progress Tracking',
      aminePersia: true,
      rosettaStone: true,
      traditionalClass: 'Manual',
      selfStudy: false
    },
    {
      feature: 'Audio Pronunciations',
      aminePersia: true,
      rosettaStone: true,
      traditionalClass: true,
      selfStudy: false
    },
    {
      feature: 'Community Forum',
      aminePersia: true,
      rosettaStone: false,
      traditionalClass: true,
      selfStudy: false
    },
    {
      feature: 'Mobile Responsive',
      aminePersia: true,
      rosettaStone: true,
      traditionalClass: false,
      selfStudy: false
    },
    {
      feature: 'Flexible Learning Schedule',
      aminePersia: true,
      rosettaStone: true,
      traditionalClass: false,
      selfStudy: true
    },
    {
      feature: 'Cost (Monthly)',
      aminePersia: '$9.99',
      rosettaStone: '$35.97',
      traditionalClass: '$200-500',
      selfStudy: '$0-50'
    },
    {
      feature: 'Time Commitment',
      aminePersia: 'Flexible',
      rosettaStone: 'Rigid schedule',
      traditionalClass: 'Fixed schedule',
      selfStudy: 'Self-directed'
    },
    {
      feature: 'Personalized Feedback',
      aminePersia: true,
      rosettaStone: true,
      traditionalClass: true,
      selfStudy: false
    },
    {
      feature: 'Grammar Explanations',
      aminePersia: true,
      rosettaStone: true,
      traditionalClass: true,
      selfStudy: 'Self-research'
    },
    {
      feature: 'Real-world Conversations',
      aminePersia: true,
      rosettaStone: true,
      traditionalClass: true,
      selfStudy: false
    },
    {
      feature: 'Writing Practice',
      aminePersia: true,
      rosettaStone: true,
      traditionalClass: true,
      selfStudy: false
    },
    {
      feature: 'Reading Comprehension',
      aminePersia: true,
      rosettaStone: true,
      traditionalClass: true,
      selfStudy: true
    }
  ];

  const categories = [
    { id: 'all', name: 'All Features', icon: '📊' },
    { id: 'cultural', name: 'Cultural Learning', icon: '🏛️' },
    { id: 'technical', name: 'Technical Features', icon: '⚙️' },
    { id: 'practical', name: 'Practical Aspects', icon: '💡' }
  ];

  const culturalFeatures = ['Native Persian Speakers', 'Cultural Context & Traditions', 'Persian Poetry (Hafez, Rumi)', 'Traditional Persian Recipes', 'Persian Holidays & Celebrations', 'Tehran City Guide & Culture'];
  const technicalFeatures = ['Interactive Flashcards', 'Adaptive Learning Algorithm', 'Progress Tracking', 'Audio Pronunciations', 'Community Forum', 'Mobile Responsive'];
  const practicalFeatures = ['Flexible Learning Schedule', 'Cost (Monthly)', 'Time Commitment', 'Personalized Feedback', 'Grammar Explanations', 'Real-world Conversations', 'Writing Practice', 'Reading Comprehension'];

  const filteredData = comparisonData.filter(item => {
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'cultural') return culturalFeatures.includes(item.feature);
    if (selectedCategory === 'technical') return technicalFeatures.includes(item.feature);
    if (selectedCategory === 'practical') return practicalFeatures.includes(item.feature);
    return true;
  });

  const renderValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <FaCheck className="text-green-500 text-xl" />
      ) : (
        <FaTimes className="text-red-500 text-xl" />
      );
    }
    return <span className="text-sm font-medium">{value}</span>;
  };

  const platforms = [
    {
      name: 'Amine Persia',
      description: 'Comprehensive Persian learning with cultural immersion',
      color: 'from-indigo-500 to-purple-600',
      rating: 5,
      pros: ['Rich cultural content', 'Native speakers', 'Flexible learning', 'Affordable pricing'],
      cons: ['Newer platform', 'Limited advanced content']
    },
    {
      name: 'Rosetta Stone',
      description: 'Premium language learning software',
      color: 'from-blue-500 to-blue-600',
      rating: 4,
      pros: ['Comprehensive course', 'Good audio quality', 'Structured learning'],
      cons: ['Very expensive', 'Rigid learning schedule', 'Limited cultural context']
    },
    {
      name: 'Traditional Classes',
      description: 'In-person or online language classes',
      color: 'from-orange-500 to-orange-600',
      rating: 4,
      pros: ['Personal interaction', 'Immediate feedback', 'Structured curriculum'],
      cons: ['Expensive', 'Fixed schedule', 'Limited flexibility']
    },
    {
      name: 'Self-Study',
      description: 'Independent learning with books and resources',
      color: 'from-gray-500 to-gray-600',
      rating: 2,
      pros: ['Low cost', 'Complete flexibility', 'Self-paced'],
      cons: ['No feedback', 'No structure', 'Limited resources']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-indigo-900 mb-4">
            🏆 Persian Learning Platform Comparison
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Discover why Amine Persia offers the most comprehensive and culturally-rich Persian learning experience compared to other platforms and methods.
          </p>
        </div>

        {/* Platform Overview Cards */}
        <div className="grid grid-cols-4 gap-4 mb-12">
          {platforms.map((platform, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className={`bg-gradient-to-r ${platform.color} p-4 text-white`}>
                <h3 className="text-lg font-bold mb-2 text-white drop-shadow-sm">{platform.name}</h3>
                <p className="text-xs opacity-95 font-medium leading-relaxed">{platform.description}</p>
                <div className="flex items-center mt-3">
                  {[...Array(5)].map((_, i) => (
                    <FaStar 
                      key={i} 
                      className={`text-sm ${i < platform.rating ? 'text-yellow-300' : 'text-gray-300'}`} 
                    />
                  ))}
                  <span className="ml-2 text-sm font-medium">{platform.rating}/5</span>
                </div>
              </div>
              <div className="p-4">
                <div className="mb-3">
                  <h4 className="font-bold text-green-700 mb-2 text-sm border-b border-green-200 pb-1">✅ Pros</h4>
                  <ul className="text-xs text-black space-y-1">
                    {platform.pros.map((pro, i) => (
                      <li key={i} className="flex items-start text-black !text-black">
                        <FaCheck className="text-green-500 text-xs mt-0.5 mr-1 flex-shrink-0" />
                        <span className="text-black !text-black">{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-red-700 mb-2 text-sm border-b border-red-200 pb-1">❌ Cons</h4>
                  <ul className="text-xs text-black space-y-1">
                    {platform.cons.map((con, i) => (
                      <li key={i} className="flex items-start text-black !text-black">
                        <FaTimes className="text-red-500 text-xs mt-0.5 mr-1 flex-shrink-0" />
                        <span className="text-black !text-black">{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                selectedCategory === category.id
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-indigo-50 border border-gray-200'
              }`}
            >
              <span className="mr-2">{category.icon}</span>
              {category.name}
            </button>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-12">
          <div className="overflow-x-auto">
            <table className="w-full" dir="ltr">
              <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold bg-indigo-700 sticky left-0 z-10 min-w-[200px]">Feature</th>
                  <th className="px-6 py-4 text-center font-semibold">
                    <div className="flex flex-col items-center">
                      <span className="text-lg">🏆</span>
                      <span>Amine Persia</span>
                    </div>
                  </th>
                  
                  <th className="px-6 py-4 text-center font-semibold">
                    <div className="flex flex-col items-center">
                      <span className="text-lg">💎</span>
                      <span>Rosetta Stone</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center font-semibold">
                    <div className="flex flex-col items-center">
                      <span className="text-lg">👨‍🏫</span>
                      <span>Traditional Classes</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center font-semibold">
                    <div className="flex flex-col items-center">
                      <span className="text-lg">📚</span>
                      <span>Self-Study</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 font-medium text-gray-800 bg-gray-100 border-r border-gray-200 sticky left-0 z-10 min-w-[200px]">{row.feature}</td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        {renderValue(row.aminePersia)}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        {renderValue(row.rosettaStone)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        {renderValue(row.traditionalClass)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        {renderValue(row.selfStudy)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Why Choose Amine Persia */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white mb-12">
          <h2 className="text-3xl font-bold mb-6 text-center text-white">Why Choose Pirooz? 🏆</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaHeart className="text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Cultural Immersion</h3>
              <p className="text-indigo-100">Learn Persian through its rich culture, poetry, and traditions</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaUsers className="text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Native Speakers</h3>
              <p className="text-indigo-100">Authentic pronunciation and cultural insights from native Persians</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaClock className="text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Flexible Learning</h3>
              <p className="text-indigo-100">Study at your own pace with 24/7 access to all content</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaDollarSign className="text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Affordable Pricing</h3>
              <p className="text-indigo-100">Premium quality at a fraction of traditional class costs</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaGlobe className="text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Comprehensive Content</h3>
              <p className="text-indigo-100">From basic grammar to advanced cultural topics</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaMobile className="text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Mobile Friendly</h3>
              <p className="text-indigo-100">Learn anywhere, anytime on any device</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Ready to Start Your Persian Journey? 🚀</h2>
          <p className="text-lg text-gray-600 mb-6">
            Join thousands of learners who have chosen Amine Persia for the most authentic and comprehensive Persian learning experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register"
              className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl inline-block text-center"
            >
              Start Free Trial
            </Link>
            <Link 
              to="/features"
              className="px-8 py-4 border-2 border-indigo-600 text-indigo-600 font-semibold rounded-lg hover:bg-indigo-600 hover:text-white transition-all duration-200 inline-block text-center"
            >
              View All Features
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Comparison; 